import { useRef, type ReactElement } from "react";

import type { BreadcrumbLeafActions } from "./GlobalBreadcrumb";

/**
 * The shape the leaf takes when it is more than a name.
 *
 * Split out of `GlobalBreadcrumb` so that file stays about how the bar is laid
 * out, and this stays about what a leaf *does*.
 */

/**
 * The leaf, while it is being renamed.
 *
 * Ported from t3code's chat-header rename, whose semantics are the ones a
 * second implementation gets wrong: blur commits unless a key already did,
 * Enter commits, Escape abandons, and an IME composition is not a submit —
 * `keyCode === 229` is how a composing Enter arrives in browsers that do not
 * set `isComposing`, and without it every CJK title commits half-typed.
 */
export function BreadcrumbLeafRenameInput({
  rename,
}: {
  readonly rename: NonNullable<BreadcrumbLeafActions["rename"]>;
}): ReactElement {
  const settled = useRef(false);

  return (
    <input
      autoFocus
      aria-label="Thread title"
      className="min-w-0 flex-1 truncate rounded-sm bg-transparent font-medium tracking-tight text-foreground outline-none ring-1 ring-ring/50 focus:ring-ring"
      data-testid="global-breadcrumb-rename"
      defaultValue={rename.value}
      onBlur={(event) => {
        if (settled.current) return;
        settled.current = true;
        rename.onCommit(event.currentTarget.value);
      }}
      onFocus={(event) => event.currentTarget.select()}
      onKeyDown={(event) => {
        if (event.nativeEvent.isComposing || event.keyCode === 229) return;
        if (event.key === "Enter") {
          settled.current = true;
          rename.onCommit(event.currentTarget.value);
        } else if (event.key === "Escape") {
          settled.current = true;
          rename.onCancel();
        }
      }}
    />
  );
}

/**
 * A crumb's project mark.
 *
 * Split out so the item stays about layout: the ladder's defaulting is about
 * identity, and folding it inline made every added field a branch here.
 */
